import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { supabase, readableError } from './supabase'
import { makeClip, canEncode } from './videoClip'
import { report } from './report'

/**
 * One clip being prepared, for the whole app.
 *
 * Preparing a clip takes fifteen to twenty seconds. Doing that inside the
 * Sharing screen meant the writer had to sit and watch it — leave the screen
 * and the component unmounts, the encoder dies, and the work is gone.
 *
 * So it lives above the router, exactly like PlayerProvider and for exactly
 * the same reason: navigating must not destroy work in progress. Start a clip
 * and then go browse the collective; a small bar keeps you posted and tells
 * you when it has landed.
 *
 * One at a time on purpose. Two hardware encoders running on a phone is slower
 * than one, not faster, and nobody is posting two clips at once.
 */

const Ctx = createContext(null)

export function ClipQueueProvider({ children }) {
  const abort = useRef(null)
  const [job, setJob] = useState(null)
  // { songId, title, phase, pct, eta, poster, error, savedFrom, savedTo }

  const patch = useCallback((p) => setJob((j) => (j ? { ...j, ...p } : j)), [])

  const cancel = useCallback(() => {
    abort.current?.abort()
    abort.current = null
    setJob(null)
  }, [])

  const dismiss = useCallback(() => setJob(null), [])

  /**
   * @param kind 'original' — the writer's own hook for their song.
   *             'cover'    — someone else's sixty seconds of a song whose
   *                          writer opened it for covers.
   * @param prepared already-encoded result from makeStillClip, for the
   *                 image-plus-recording path. Skips the encode, nothing else.
   * @param clipWindow {start,end} the writer chose on the filmstrip. Null means
   *               the first MAX_SECONDS, which is what every caller used to
   *               get whether they wanted it or not.
   * Same cap, same upload. Only who owns the result differs.
   */
  const start = useCallback(async (file, song, onDone, kind = 'original', prepared = null, clipWindow = null) => {
    if (!file) return
    abort.current = new AbortController()
    const signal = abort.current.signal
    setJob({
      songId: song.id,
      title: song.title || 'Untitled',
      kind,
      phase: 'preparing',
      pct: 0, eta: null, poster: null, error: null,
    })

    // Declared out here so the catch can report what was actually being sent
    // when it failed -- size and whether it had been encoded are most of the
    // diagnosis, and they are exactly what is out of scope inside the try.
    let f = file
    let made = null

    try {

      // A still clip arrives already encoded: the image and the recording were
      // muxed before the queue saw them, because that path has to show the
      // writer a preview of the exact frame first. Re-encoding here would be a
      // second lossy pass over a picture that has not moved.
      if (prepared) {
        made = prepared
        f = made.file
      } else if (canEncode()) {
        made = await makeClip(file, {
          signal,
          start: clipWindow?.start ?? 0,
          end: clipWindow?.end ?? null,
          onPoster: (poster) => patch({ poster }),
          onProgress: (p, eta) => patch({ pct: Math.round(p * 100), eta }),
        })
        if (made) f = made.file
      }

      if (!made && f.size > 100 * 1024 * 1024) {
        throw new Error(
          "That video is too big and this browser can't shrink it — " +
          'trim it to about a minute first, or try from your phone.',
        )
      }
      if (signal.aborted) return

      patch({ phase: 'posting', pct: 0, eta: null })

      const { data, error } = await supabase.functions.invoke('r2-upload-url', {
        body: {
          song_id: song.id,
          variant: kind === 'cover' ? 'cover' : 'clip',
          content_type: f.type || 'video/mp4',
          filename: f.name,
          size_bytes: f.size,
        },
      })
      if (error) throw error
      if (data?.error) throw new Error(data.error)

      await new Promise((resolve, reject) => {
        const req = new XMLHttpRequest()
        signal.addEventListener('abort', () => req.abort())
        req.open('PUT', data.upload_url, true)
        req.setRequestHeader('Content-Type', data.content_type)
        req.upload.onprogress = (e) => {
          if (e.lengthComputable) patch({ pct: Math.round((e.loaded / e.total) * 100) })
        }
        req.onload = () => (req.status >= 200 && req.status < 300
          ? resolve() : reject(new Error(`Upload failed (${req.status})`)))
        // XHR reports a blocked cross-origin request and a dropped connection
        // identically: status 0, no headers, no detail. The one thing that
        // separates them is which origin asked, so the error carries it -- an
        // app served from a deploy alias or a home-screen PWA is on a different
        // origin than the bucket's CORS rules may list, and that reads to the
        // user as "check your connection" forever.
        req.onerror = () => reject(new Error(
          `Upload failed from ${location.origin} — this is either the `
          + 'connection or the storage bucket refusing this origin.',
        ))
        req.onabort = () => reject(new Error('Cancelled'))
        req.send(f)
      })

      // getSession() refreshes a token that expired while the upload was
      // running. Who owns the clip is no longer decided here -- clip-commit
      // takes it from the verified JWT -- but there is no point calling it
      // without a session, and this says so in a sentence the writer can act on.
      const { data: sess } = await supabase.auth.getSession()
      if (!sess?.session) {
        throw new Error(
          'Your session expired while that was uploading — sign in again and '
          + 'add the clip once more.',
        )
      }
      /**
       * The row is written by the `clip-commit` edge function, not from here.
       *
       * Writing it directly never once worked in production: the upload landed
       * and the insert came back "new row violates row-level security policy",
       * with a valid, freshly refreshed token belonging to the writer who owned
       * the song -- while the identical insert, run against the same database
       * as `authenticated` with that same uid, succeeded. Three client-side
       * fixes chased that and none of them were it.
       *
       * The function re-checks ownership, reach and the object key explicitly
       * before writing, so nothing is trusted that the policy would not have
       * trusted. It also closes the gap that made this so unpleasant: the
       * upload had already succeeded every time, leaving the file in storage
       * with nothing pointing at it.
       */
      const { data: commit, error: e2 } = await supabase.functions
        .invoke('clip-commit', {
          body: {
            song_id: song.id,
            object_key: data.object_key,
            kind,
            duration_seconds: made?.seconds ?? null,
            width: made?.width ?? null,
            height: made?.height ?? null,
          },
        })
      if (e2) throw e2
      if (commit?.error) throw new Error(commit.error)
      const created = commit?.clip ?? null

      onDone?.(created)
      patch({ phase: 'done', pct: 100, savedFrom: made?.from, savedTo: made?.to })
      // The "it's up" state is a result, not a status. Clear it on its own so
      // it doesn't sit on screen for the rest of the session.
      setTimeout(() => setJob((j) => (j?.phase === 'done' ? null : j)), 6000)
    } catch (e) {
      if (e.message === 'Cancelled') return setJob(null)
      // The clip path used to fail entirely in private: it caught this, showed
      // one sentence, and told nobody. A writer saw "check your connection" and
      // Sentry saw nothing at all, so the first real upload failure in
      // production was invisible. Ids and states only -- never the file, the
      // title, or anything typed.
      // Enough about the session to tell an expired token from a missing one
      // without ever carrying the token itself. `exp` is a number of seconds.
      let tokenAge = null
      try {
        const { data: s2 } = await supabase.auth.getSession()
        tokenAge = s2?.session
          ? { expiresAt: s2.session.expires_at, now: Math.floor(Date.now() / 1000),
              uid: s2.session.user?.id ?? null }
          : 'no session'
      } catch { tokenAge = 'unreadable' }

      report(e, 'clip-upload', {
        songId: song.id,
        kind,
        phase: 'upload',
        code: e?.code ?? null,
        session: tokenAge,
        // Sentry's PII scrubber redacts an extra called `origin`, which is
        // how the one field that identified this as a CORS refusal arrived as
        // "[Filtered]". The host under a different name survives.
        appHost: location.host,
        encoded: !!made,
        bytes: f?.size ?? null,
        canEncode: canEncode(),
      })
      patch({ phase: 'failed', error: readableError(e) })
    } finally {
      abort.current = null
    }
  }, [patch])

  const value = useMemo(() => ({ job, start, cancel, dismiss }), [job, start, cancel, dismiss])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useClipQueue() {
  const v = useContext(Ctx)
  if (!v) throw new Error('useClipQueue must be used inside ClipQueueProvider')
  return v
}
